// SCR-STAFF-902 (بخش برند و سربرگ — US-PLT-070): لوگو (فقط PNG یا SVG تا ۵۰۰ KB؛ AC-PLT-070.2)، نام کوتاه (فرستنده پیامک)،
// رنگ اصلی و سربرگ و پانویس چاپ در سطح مستأجر با بازنویسی سربرگ در سطح شعبه (AC-PLT-070.1). بررسی امنیت SVG سمت سرور است.
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type BrandingView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, LoadingState, TextFieldRHF } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CenterProfilePage } from './CenterProfilePage';
import { pltKeys } from './common';

export const LOGO_MAX_BYTES = 500 * 1024;
export const LOGO_TYPES = ['image/png', 'image/svg+xml'] as const;
const COLOR = /^#[0-9a-fA-F]{6}$/;

interface BrandingForm {
  shortName: string;
  primaryColor: string;
  headerText: string;
  footerText: string;
}

function toForm(view: BrandingView | undefined): BrandingForm {
  return {
    shortName: view?.shortName ?? '',
    primaryColor: view?.primaryColor ?? '',
    headerText: view?.headerText ?? '',
    footerText: view?.footerText ?? '',
  };
}

const orNull = (value: string) => (value.trim() === '' ? null : value.trim());

/** بررسی Client هم‌ارز سرور (نوع اعلام‌شده و اندازه)؛ امضای فایل و امنیت SVG را سرور می‌سنجد. */
export function logoProblem(file: File): 'type' | 'size' | null {
  if (!(LOGO_TYPES as readonly string[]).includes(file.type)) {
    return 'type';
  }
  return file.size > LOGO_MAX_BYTES ? 'size' : null;
}

function BranchLetterhead({ view, canWrite }: { view: BrandingView; canWrite: boolean }) {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const branches = useQuery({
    queryKey: pltKeys.branches(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/branches')),
  });
  const [branchId, setBranchId] = useState('');
  const override = view.branches.find((item) => item.branchId === branchId);
  const [header, setHeader] = useState('');
  const [footer, setFooter] = useState('');
  useEffect(() => {
    setHeader(override?.headerText ?? '');
    setFooter(override?.footerText ?? '');
  }, [override, branchId]);

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/platform/branding/branches/{branchId}', {
          params: { path: { branchId } },
          body: {
            shortName: null,
            primaryColor: null,
            headerText: orNull(header),
            footerText: orNull(footer),
          },
        }),
      ),
    onSuccess: (updated) => queryClient.setQueryData(pltKeys.branding(session?.tenantId), updated),
  });

  const items = branches.data?.items ?? [];
  if (items.length === 0) {
    return null;
  }
  return (
    <Stack spacing={2} data-testid="branch-letterhead">
      <Typography variant="subtitle2">{t('plt:branding.branch_title')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('plt:branding.branch_help')}
      </Typography>
      <TextField
        select
        label={t('plt:branding.branch')}
        value={branchId}
        onChange={(event) => setBranchId(event.target.value)}
        fullWidth
        data-testid="letterhead-branch"
      >
        {items.map((branch) => (
          <MenuItem key={branch.id} value={branch.id}>
            {branch.name}
            {view.branches.some((item) => item.branchId === branch.id)
              ? ` · ${t('plt:branding.overridden')}`
              : ''}
          </MenuItem>
        ))}
      </TextField>
      {branchId && (
        <>
          <TextField
            label={t('plt:branding.header_text')}
            value={header}
            onChange={(event) => setHeader(event.target.value)}
            multiline
            minRows={2}
            fullWidth
            disabled={!canWrite}
            helperText={t('plt:branding.branch_empty_help')}
          />
          <TextField
            label={t('plt:branding.footer_text')}
            value={footer}
            onChange={(event) => setFooter(event.target.value)}
            multiline
            minRows={2}
            fullWidth
            disabled={!canWrite}
          />
          {save.error ? <ErrorState error={save.error} compact /> : null}
          <Box>
            <Button
              variant="outlined"
              onClick={() => save.mutate()}
              disabled={!canWrite || save.isPending}
              data-testid="letterhead-branch-save"
            >
              {t('plt:branding.save_branch')}
            </Button>
          </Box>
        </>
      )}
    </Stack>
  );
}

export function BrandingSection() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('plt.settings.manage');
  const fileInput = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<'type' | 'size' | null>(null);
  const [saved, setSaved] = useState(false);

  const branding = useQuery({
    queryKey: pltKeys.branding(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/branding')),
  });
  const values = useMemo(() => toForm(branding.data), [branding.data]);
  const { control, handleSubmit, formState, setError } = useForm<BrandingForm>({ values });

  const onSaved = (view: BrandingView) => {
    queryClient.setQueryData(pltKeys.branding(session?.tenantId), view);
    setSaved(true);
  };

  const save = useMutation({
    mutationFn: (form: BrandingForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/platform/branding', {
          body: {
            shortName: orNull(form.shortName),
            primaryColor: orNull(form.primaryColor),
            headerText: orNull(form.headerText),
            footerText: orNull(form.footerText),
          },
        }),
      ),
    onMutate: () => setSaved(false),
    onSuccess: onSaved,
  });
  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return unwrap(
        tenantApi.PUT('/api/v1/platform/branding/logo', {
          body: form as never,
          bodySerializer: (body: unknown) => body as FormData,
        }),
      );
    },
    onSuccess: onSaved,
  });
  const removeLogo = useMutation({
    mutationFn: () => unwrap(tenantApi.DELETE('/api/v1/platform/branding/logo')),
    onSuccess: onSaved,
  });

  const submit = handleSubmit((form) => {
    if (form.primaryColor.trim() && !COLOR.test(form.primaryColor.trim())) {
      setError('primaryColor', { message: 'plt:branding.color_invalid' });
      return;
    }
    save.mutate(form);
  });

  const onFile = (file: File | undefined) => {
    if (!file) {
      return;
    }
    const problem = logoProblem(file);
    setFileError(problem);
    if (!problem) {
      upload.mutate(file);
    }
  };

  const view = branding.data;
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mt: 3 }}
      data-testid="branding-section"
      component="section"
      aria-labelledby="branding-title"
    >
      <Typography variant="h6" id="branding-title" sx={{ mb: 1 }}>
        {t('plt:branding.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('plt:branding.subtitle')}
      </Typography>
      {branding.isPending ? (
        <LoadingState rows={2} />
      ) : branding.error ? (
        <ErrorState error={branding.error} onRetry={() => void branding.refetch()} />
      ) : view ? (
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              data-testid="logo-state"
              color={view.hasLogo ? 'success' : 'default'}
              label={view.hasLogo ? t('plt:branding.logo_present') : t('plt:branding.logo_missing')}
            />
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/svg+xml"
              hidden
              aria-label={t('plt:branding.logo_file')}
              data-testid="logo-input"
              onChange={(event) => {
                onFile(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
            <Button
              variant="outlined"
              startIcon={<UploadFileOutlinedIcon />}
              onClick={() => fileInput.current?.click()}
              disabled={!write.canWrite || upload.isPending}
              data-testid="logo-upload"
            >
              {t('plt:branding.logo_upload')}
            </Button>
            {view.hasLogo && (
              <Button
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => removeLogo.mutate()}
                disabled={!write.canWrite || removeLogo.isPending}
                data-testid="logo-remove"
              >
                {t('plt:branding.logo_remove')}
              </Button>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t('plt:branding.logo_help')}
          </Typography>
          {fileError && (
            <Alert severity="error" data-testid="logo-error" data-reason={fileError}>
              {t(`plt:branding.logo_${fileError}`)}
            </Alert>
          )}
          {upload.error ? <ErrorState error={upload.error} compact /> : null}
          <Divider />
          <Box component="form" noValidate onSubmit={(event) => void submit(event)}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextFieldRHF
                  control={control}
                  name="shortName"
                  labelKey="plt:branding.short_name"
                  helperKey="plt:branding.short_name_help"
                  fullWidth
                  disabled={!write.canWrite}
                  slotProps={{ htmlInput: { maxLength: 30, 'data-testid': 'branding-short-name' } }}
                />
                <TextFieldRHF
                  control={control}
                  name="primaryColor"
                  labelKey="plt:branding.primary_color"
                  helperKey="plt:branding.primary_color_help"
                  fullWidth
                  disabled={!write.canWrite}
                  slotProps={{ htmlInput: { dir: 'ltr', maxLength: 7, 'data-testid': 'branding-color' } }}
                />
              </Stack>
              <TextFieldRHF
                control={control}
                name="headerText"
                labelKey="plt:branding.header_text"
                fullWidth
                multiline
                minRows={2}
                disabled={!write.canWrite}
                slotProps={{ htmlInput: { 'data-testid': 'branding-header' } }}
              />
              <TextFieldRHF
                control={control}
                name="footerText"
                labelKey="plt:branding.footer_text"
                fullWidth
                multiline
                minRows={2}
                disabled={!write.canWrite}
              />
              {save.error ? <ErrorState error={save.error} compact /> : null}
              {saved && !formState.isDirty && (
                <Alert severity="success" role="status" data-testid="branding-saved">
                  {t('plt:branding.saved')}
                </Alert>
              )}
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!write.canWrite || save.isPending}
                  data-testid="branding-save"
                >
                  {t('plt:branding.save')}
                </Button>
              </Box>
            </Stack>
          </Box>
          <Divider />
          <BranchLetterhead view={view} canWrite={write.canWrite} />
        </Stack>
      ) : null}
    </Paper>
  );
}

/** SCR-STAFF-902: اطلاعات مرکز (AP-PLT-02) و بخش برند و سربرگ (AP-PLT-05) در یک صفحه. */
export function CenterAndBrandingPage() {
  return (
    <>
      <CenterProfilePage />
      <BrandingSection />
    </>
  );
}
